import set from "@rill/set";
import { round } from "lodash";
import moment from "moment";
import ActivityDrawer from "../../../../parts/activity-drawer/ActivityDrawer";
import FilterBlockReviewModal from "../../../../parts/modals/filter-block-review";
import JWPlayerVideoModal from "../../../../parts/modals/jwplayer-video";
import { formattedTarget } from "../../../../utils/actuals";
import {
  buildVideoModalUrl,
  findVideoNeighbors,
} from "../../../../utils/video-modal-url";
import BlockReview from "./BlockReview";

const BlockReviewController = (app) => {
  app.get(
    "/block-review",
    set("heading", "Block Review"),
    async ({ req, res, locals, load }) => {
      const { query } = req;
      const { from, to } = query;
      const modal = query.modal || {};

      const athleteId = locals.athlete
        ? locals.athlete._id
        : locals.activeUser._id;

      const fromDate = from
        ? from
        : moment().subtract(4, "weeks").format("YYYY-MM-DD");
      const toDate = to ? to : moment().format("YYYY-MM-DD");

      const loadPromises = [
        load("weeksExercises", {
          from: fromDate,
          to: toDate,
          proxyAthlete: athleteId,
        }),
        load("dayPersonalRecords", {
          start: fromDate,
          end: toDate,
          proxyAthlete: athleteId,
          isCheckIn: true,
        }),
      ];

      if (locals.athlete) {
        loadPromises.push(load("clientConfiguration", { proxyAthlete: athleteId }));
      }

      await Promise.all(loadPromises);

      if (query.threadId) {
        await load("comments", {
          threadId: query.threadId,
        });
      } else {
        locals.comments = [];
      }

      // Build prev/next sequence within this exercise's videos
      let prevHref;
      let nextHref;
      if (modal.type === "exercise-set-video" && query.exerciseToEdit) {
        const targetExercise = (locals.weeksExercises || []).find(
          (ex) => String(ex._id) === String(query.exerciseToEdit)
        );
        if (targetExercise) {
          const sequence = [];
          const exerciseId = String(targetExercise._id);
          const primary =
            targetExercise.primary ?? targetExercise.exercise?.primary ?? "";
          const secondary =
            targetExercise.secondary ??
            targetExercise.exercise?.secondary ??
            "";
          const exType =
            targetExercise.type ?? targetExercise.exercise?.type ?? "";
          const movement =
            targetExercise.movement ?? targetExercise.exercise?.movement ?? "";
          const sessionIndex = 0;
          let cummulativeSets = 0;
          (targetExercise.sets || []).forEach((set, setIdx) => {
            const numRounds = Number(set.sets || set.rounds || 1);
            const videos = set.videos || {};
            for (let i = 0; i < numRounds; i++) {
              const video = videos[i];
              if (video?.videoOriginal) {
                const outcomeIndex = cummulativeSets + i;
                const seqOutcomeObj =
                  set.outcomes?.[cummulativeSets + i]?.outcome || {};
                const seqIsPowerlifting =
                  exType === "primary" || exType === "secondary";
                const { units, intensityMeasure } = locals.activeUser;
                const seqPrecision = units === "metric" ? 2 : 1;
                const seqUserMetrics = units === "imperial" ? "lb" : "kg";
                const seqRawLoad = seqOutcomeObj.load;
                const seqLoad = seqRawLoad
                  ? `${round(locals.fromMetric(seqRawLoad, units), seqPrecision)} ${seqUserMetrics}`
                  : 0;
                const seqTarget =
                  seqRawLoad || seqOutcomeObj.reps || seqOutcomeObj.rpe
                    ? formattedTarget(
                        seqOutcomeObj,
                        seqIsPowerlifting,
                        set.percentRepMax || undefined,
                        units,
                        seqLoad,
                        intensityMeasure,
                        intensityMeasure !== "rpe",
                        true
                      ) || undefined
                    : undefined;
                const seqSetNumber = setIdx + 1;
                sequence.push({
                  key: `${exerciseId}:${sessionIndex}:${outcomeIndex}`,
                  href: buildVideoModalUrl({
                    modalType: "exercise-set-video",
                    video,
                    exerciseToEdit: exerciseId,
                    setIndex: sessionIndex,
                    videoIndex: outcomeIndex,
                    utcDate: targetExercise.utcDate,
                    primary,
                    secondary,
                    type: exType,
                    movement,
                    setNumber: seqSetNumber,
                    setTarget: seqTarget,
                    setComment:
                      set.comments?.[cummulativeSets + i] || undefined,
                  }),
                });
              }
            }
            cummulativeSets += numRounds;
          });
          const currentKey = `${query.exerciseToEdit}:${Number(query.setIndex)}:${Number(query.videoIndex)}`;
          ({ prevHref, nextHref } = findVideoNeighbors({
            sequence,
            currentKey,
          }));
        }
      }

      locals.modal = {
        "filter-block-review-modal": (
          <FilterBlockReviewModal
            filtersModal={query.filtersModal}
            exerciseFilters={query.exerciseFilters}
          />
        ),
        "exercise-set-video": (
          <JWPlayerVideoModal
            athleteId={query.uploaderId || query.athlete}
            exerciseToEdit={query.exerciseToEdit}
            locals={locals}
            mediaId={query.mediaId}
            originalId={query.originalId}
            setIndex={query.setIndex}
            threadId={query.threadId}
            reviewedByTheCoach={query.reviewedByTheCoach}
            utcDate={query.utcDate}
            videoIndex={query.videoIndex}
            primary={query.primary}
            secondary={query.secondary}
            type={query.type}
            movement={query.movement}
            prevHref={prevHref}
            nextHref={nextHref}
            setNumber={query.setNumber}
            setTarget={query.setTarget}
            setComment={query.setComment}
          />
        ),
        "activity-feed": <ActivityDrawer />,
      }[modal.type];

      res.body = <BlockReview locals={locals} />;
    }
  );
};

export default BlockReviewController;
